import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getCategories } from "@/lib/content";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "话题归档", to: "/topics" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled;
  const categories = getCategories();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  const linkBase = "text-sm transition-colors duration-200 whitespace-nowrap";
  const activeClass = solid ? "text-primary-600 font-medium" : "text-background-50 font-medium";
  const idleClass = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-100/85 hover:text-background-50";

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 py-3 backdrop-blur"
          : "border-b border-transparent bg-transparent py-5"
      }`}
    >
      <div className="flex w-full items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5" aria-label="黑料吃话 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-book-marked-line text-lg" />
          </span>
          <span
            className={`font-heading text-lg font-bold tracking-wide ${
              solid ? "text-foreground-950" : "text-background-50"
            }`}
          >
            黑料吃话
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="主导航">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`${linkBase} ${isActive(link.to) ? activeClass : idleClass}`}
            >
              {link.label}
            </Link>
          ))}

          <div className="group relative">
            <button
              type="button"
              className={`${linkBase} ${idleClass} flex cursor-pointer items-center gap-1`}
              aria-haspopup="true"
            >
              主题分类
              <i className="ri-arrow-down-s-line text-base transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2 shadow-none">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/topics?category=${cat.id}`}
                    className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    <i className={`${cat.icon} text-base text-secondary-500`} />
                    <span>{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/about#contact"
            className={`hidden whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors md:inline-flex ${
              solid
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "bg-background-50 text-foreground-950 hover:bg-background-100"
            }`}
          >
            联系编辑部
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="打开导航菜单"
            aria-expanded={mobileOpen}
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border text-lg lg:hidden ${
              solid
                ? "border-background-300 text-foreground-800"
                : "border-background-50/40 text-background-50"
            }`}
          >
            <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="mt-3 border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-100"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="mt-4 mb-2 px-3 text-xs tracking-[0.2em] text-secondary-500">主题分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/topics?category=${cat.id}`}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className={`${cat.icon} text-base text-secondary-500`} />
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
          <Link
            to="/about#contact"
            className="mt-4 flex items-center justify-center rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50"
          >
            联系编辑部
          </Link>
        </div>
      ) : null}
    </header>
  );
}